import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { BadgeCheck, XCircle, MinusCircle, ArrowDown, ArrowRight } from "lucide-react";
import { useLenis, scrollToSection } from "@/App";
import { SINGLES } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusBadge = ({ status, testId }) => {
    if (status === "nomatch")
        return (
            <span data-testid={testId} className="flex items-center gap-1.5 font-mono text-[10px] font-bold px-2.5 py-1 rounded-full bg-slate-500/10 text-slate-400 border border-slate-500/30">
                <MinusCircle className="w-3 h-3" /> NO MATCH
            </span>
        );
    if (status === "lost")
        return (
            <span data-testid={testId} className="flex items-center gap-1.5 font-mono text-[10px] font-bold px-2.5 py-1 rounded-full bg-red-400/10 text-red-400 border border-red-400/30">
                <XCircle className="w-3 h-3" /> LOST
            </span>
        );
    return (
        <span data-testid={testId} className="flex items-center gap-1.5 font-mono text-[10px] font-bold px-2.5 py-1 rounded-full bg-bn-green/10 text-bn-green border border-bn-green/30">
            <BadgeCheck className="w-3 h-3" /> WON
        </span>
    );
};

const SingleCard = ({ single, index }) => (
    <motion.article
        data-testid={`single-card-${index + 1}`}
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.55, delay: (index % 4) * 0.08 }}
        className="group relative bg-bn-card/70 border border-bn-line rounded-xl p-5 hover:border-bn-blue/50 hover:-translate-y-1.5 hover:shadow-[0_14px_40px_rgba(0,102,255,0.18)] transition-all duration-300"
    >
        <div className="flex items-center justify-between mb-4">
            <span className="font-mono text-xs text-slate-400 tracking-wider">{single.date}</span>
            <StatusBadge status={single.status} testId={`single-status-${index + 1}`} />
        </div>
        {single.status === "nomatch" ? (
            <div className="py-5 text-center font-mono text-xs tracking-[0.2em] text-slate-600">NO MATCH SCHEDULED</div>
        ) : (
            <>
                <div className="text-sm font-semibold text-slate-100 leading-snug min-h-[40px]">{single.teams}</div>
                <div className="mt-2 flex items-center justify-between gap-2">
                    <span className="font-mono text-[11px] font-semibold px-2 py-1 rounded bg-bn-blue/10 text-bn-cyan border border-bn-blue/25">
                        {single.pick} ({Number(single.odds).toFixed(2)})
                    </span>
                    <span className="font-mono text-[11px] text-slate-500">{single.score}</span>
                </div>
            </>
        )}
        <div className="mt-4 pt-3.5 border-t border-bn-line flex items-center justify-between">
            <span className="font-mono text-[10px] tracking-[0.2em] text-slate-500">SINGLE ODDS</span>
            <span data-testid={`single-odds-${index + 1}`} className="font-display font-extrabold text-xl text-bn-blue group-hover:text-glow-blue transition-all">
                {single.odds ? Number(single.odds).toFixed(2) : "—"}
            </span>
        </div>
    </motion.article>
);

export const Singles = () => {
    const lenisRef = useLenis();
    const [singles, setSingles] = useState(SINGLES);
    const [limit, setLimit] = useState(20);
    const [shown, setShown] = useState(4);

    useEffect(() => {
        axios
            .get(`${API}/singles`)
            .then((res) => {
                if (Array.isArray(res.data) && res.data.length) setSingles(res.data);
            })
            .catch(() => {});
        axios
            .get(`${API}/content`)
            .then((res) => {
                if (res.data?.display_limits?.singles) setLimit(res.data.display_limits.singles);
            })
            .catch(() => {});
    }, []);

    return (
        <section id="singles" data-testid="singles-section" className="relative py-20 lg:py-32">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                    className="text-center mb-14"
                >
                    <div className="font-mono text-[11px] tracking-[0.3em] text-bn-blue font-semibold mb-4">OFFER 1 · DAILY SINGLES</div>
                    <h2 data-testid="singles-heading" className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white">
                        LATEST WINNING <span className="text-bn-blue text-glow-blue">SINGLES</span>
                    </h2>
                    <p className="mt-4 text-base text-slate-400 max-w-xl mx-auto">
                        Singles with odds between 1.90 and 2.50 — the latest {limit} picks received by our subscribers.
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {singles.slice(0, shown).map((s, i) => (
                        <SingleCard key={s.single_id || s.date} single={s} index={i} />
                    ))}
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                    className="mt-12 text-center"
                >
                    {shown < Math.min(singles.length, limit) ? (
                        <button
                            data-testid="singles-view-more-button"
                            onClick={() => setShown((s) => s + 4)}
                            className="group inline-flex items-center gap-2.5 px-8 py-4 rounded-full border border-white/15 hover:border-bn-blue/60 text-slate-200 hover:text-white font-semibold text-sm tracking-wide transition-all duration-300 hover:bg-bn-blue/10"
                        >
                            VIEW MORE
                            <ArrowDown className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-1" />
                        </button>
                    ) : (
                        <button
                            data-testid="singles-subscribe-archive-button"
                            onClick={() => scrollToSection(lenisRef.current, "subscription")}
                            className="group inline-flex items-center gap-2.5 px-8 py-4 rounded-full bg-bn-blue hover:bg-bn-bluehover text-white font-semibold text-sm tracking-wide transition-all duration-300 hover:shadow-[0_0_35px_rgba(0,102,255,0.55)]"
                        >
                            SUBSCRIBE AND SEE FULL ARCHIVE
                            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </button>
                    )}
                </motion.div>
            </div>
        </section>
    );
};
